import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { useApplicationConfig } from '@bonusly/application';
import { Box, Button, Container, Display, Spinner, Stack, Text } from '@bonusly/design-system';
import { joinPath } from '@bonusly/http';

import { useTrialStatus } from '../../../hooks/use-trial-status';
import { TrialWhatsNextPayload, useDismissTrialWhatsNext, useTrialWhatsNext } from '../../../queries/trial-whats-next';
import { PlanCards } from './plan-cards';

// Both CTAs hand off to the existing Explore Plans modal on the billing page.
// That modal is opened by a frontend-read query param (`explore_plans=true`)
// and already handles both upgrading to Team (with payment) and downgrading to
// Free, so we reuse it as-is rather than introducing a new checkout. A more
// direct one-click path is deferred to the simplified-checkout follow-up.
const EXPLORE_PLANS_PATH = '/company/billing?explore_plans=true';

interface TrialWhatsNextViewProps {
	data: TrialWhatsNextPayload;
	// Called when the user acts on a CTA, before navigating away, so the surface
	// can dismiss the current milestone (and the interstitial can close). Returns
	// a Promise the view awaits so the dismissal POST isn't cancelled by the
	// page unload. Resolves even on failure so a failed dismissal never blocks
	// the CTA's navigation.
	onActed?: () => Promise<void>;
	// Tightens vertical spacing and headline size for the login interstitial,
	// which must fit the viewport so the dismiss CTA stays visible without
	// scrolling. The standalone route leaves this false for a roomier layout.
	compact?: boolean;
}

/**
 * Presentation of the recommendation, given a resolved payload. Split out from
 * the data-fetching wrapper so the interstitial can render the same view with a
 * payload it already has, and so tests can drive it with props.
 *
 * Timing copy (pill, headline) comes from `useTrialStatus` so it stays in
 * lockstep with the admin trial banner and navbar card. The cached API payload
 * drives usage stats, plan cards, and analytics milestone metadata.
 */
export const TrialWhatsNextView: FC<TrialWhatsNextViewProps> = ({ data, onActed, compact = false }) => {
	const { t } = useTranslation('trial_whats_next');
	const { track } = useAnalytics();
	const recognizeOrigin = useApplicationConfig('recognizeOrigin');
	const { daysLeft, isPostExpiry } = useTrialStatus();

	// Pill copy is derived from live who-am-i timing during render (no effects),
	// matching the banner and navbar card. Pre-expiry uses i18n pluralization on
	// `daysLeft`; the count of 0 resolves to "Trial ends today" via the explicit
	// `_zero` plural so we never render "in 0 days". Post-expiry switches to the
	// ended-state copy.
	const pillCount = Math.max(0, daysLeft ?? 0);
	const pillText = isPostExpiry ? t('pill.post_expiry') : t('pill.pre_expiry', { count: pillCount });

	// A zero-recognition usage line ("your team gave 0 recognitions across 0
	// people") undercuts the "your program is working" headline, so we omit the
	// whole sentence in that case. Derived during render, no effect.
	const showUsage = data.usage.recognitionsCount > 0;

	// `windowDays` floors to whole days, so a trial started today reports 0. The
	// "in the last 0 days" framing reads badly for the common brand-new-trial
	// case, so we pick a "So far, ..." variant (no days clause) at zero and the
	// standard "In the last N days, ..." variant otherwise. Selected during
	// render straight from the payload, no effect.
	const usageKey = data.usage.windowDays === 0 ? 'usage.sentence_today' : 'usage.sentence';

	const handleContinueOnTeam = async () => {
		track(AnalyticsEvent.TrialWhatsNext.ContinueOnTeamClicked, { phase: data.phase, milestone: data.milestone });
		await onActed?.();
		window.location.href = joinPath(recognizeOrigin, EXPLORE_PLANS_PATH);
	};

	const handleSwitchToFree = async () => {
		track(AnalyticsEvent.TrialWhatsNext.SwitchToFreeClicked, { phase: data.phase, milestone: data.milestone });
		await onActed?.();
		window.location.href = joinPath(recognizeOrigin, EXPLORE_PLANS_PATH);
	};

	return (
		<Container maxW="container.lg" py={compact ? 6 : 10}>
			<Stack gap={compact ? 4 : 6}>
				<Text
					alignSelf="flex-start"
					borderRadius="full"
					px={3}
					py={1}
					fontSize="sm"
					fontWeight="semibold"
					bgColor={isPostExpiry ? 'error.bg' : 'hashtag.bgAlpha'}
					color={isPostExpiry ? 'error.text' : 'text.hashtag'}
					data-testid="trial-whats-next-status-pill"
				>
					{pillText}
				</Text>

				<Box>
					<Display size={compact ? 'md' : 'lg'} color="text.default" data-testid="trial-whats-next-headline">
						{isPostExpiry ? t('headline.post_expiry') : t('headline.pre_expiry')}
					</Display>
					{showUsage && (
						<Text mt={compact ? 2 : 3} color="text.softer" fontSize={compact ? 'md' : 'lg'} data-testid="trial-whats-next-usage">
							{t(usageKey, {
								windowDays: data.usage.windowDays,
								recognitionsCount: data.usage.recognitionsCount,
								participantsCount: data.usage.participantsCount,
							})}
						</Text>
					)}
				</Box>

				<PlanCards freeOption={data.freeOption} onContinueOnTeam={handleContinueOnTeam} onSwitchToFree={handleSwitchToFree} compact={compact} />
			</Stack>
		</Container>
	);
};

/**
 * Route-level wrapper for `/trial/whats-next`. Fetches the recommendation and
 * renders nothing when the endpoint 404s (company not in trial / not recently
 * expired), matching the `useBizyBillingSettings` "null means render nothing"
 * convention. Tracks a `Viewed` event once the payload resolves.
 */
export const TrialWhatsNextPage: FC = () => {
	const { isInScope, canSeeWhatsNext } = useTrialStatus();
	const eligible = isInScope && canSeeWhatsNext;
	const { data, isLoading, isError, refetch } = useTrialWhatsNext({ enabled: eligible });
	const { t } = useTranslation('trial_whats_next');
	const { track } = useAnalytics();
	const { mutateAsync: dismissMilestone } = useDismissTrialWhatsNext();

	// CTA clicks from this route dismiss the current milestone too, so the login
	// interstitial doesn't re-present after the admin already acted here. The
	// catch keeps a failed dismissal from blocking the CTA's navigation.
	const handleActed = () => dismissMilestone().catch(() => undefined);

	// Fire `Viewed` once the payload resolves, exactly once per mount, only when
	// the page would actually render (matches TrialBanner / TrialCard gating).
	const hasTrackedRef = useRef(false);
	useEffect(() => {
		if (!data || !isInScope || !canSeeWhatsNext || hasTrackedRef.current) return;
		hasTrackedRef.current = true;
		track(AnalyticsEvent.TrialWhatsNext.Viewed, { phase: data.phase, milestone: data.milestone, surface: 'route' });
	}, [data, isInScope, canSeeWhatsNext, track]);

	// Cheap client-side pre-filter: superadmin-initialized companies, out-of-
	// window trials, and non-finance admins render nothing (same as auto-present).
	if (!eligible) return null;

	if (isLoading) {
		return (
			<Box display="flex" justifyContent="center" py={20}>
				<Spinner data-testid="trial-whats-next-loading" />
			</Box>
		);
	}

	if (isError) {
		return (
			<Box display="flex" flexDirection="column" alignItems="center" gap={4} py={20} data-testid="trial-whats-next-error">
				<Text color="text.softer">{t('error.load_failed')}</Text>
				<Button variant="outline" size="sm" onClick={() => refetch()}>
					{t('error.retry')}
				</Button>
			</Box>
		);
	}

	if (!data) return null;

	return <TrialWhatsNextView data={data} onActed={handleActed} />;
};

/**
 * Auto-presented interstitial variant. Dismissing (or acting on a CTA) calls the
 * server-authoritative dismiss endpoint via `useDismissTrialWhatsNext`, which
 * records the current milestone so the backend stops setting `shouldAutoPresent`
 * and patches the in-session cache. Pre-expiry is dismissible; post-expiry hides
 * the dismiss affordance to stay prominent.
 */
interface TrialWhatsNextInterstitialProps {
	data: TrialWhatsNextPayload;
	onClose: () => void;
}

export const TrialWhatsNextInterstitial: FC<TrialWhatsNextInterstitialProps> = ({ data, onClose }) => {
	const { t } = useTranslation('trial_whats_next');
	const { track } = useAnalytics();
	const { mutateAsync: dismissMilestone } = useDismissTrialWhatsNext();
	const { isPostExpiry } = useTrialStatus();

	// Dismissal is best-effort: we swallow failures so a failed POST never leaves
	// the user stuck on the interstitial, nor (via `onActed`) blocks the CTA's
	// navigation.
	const handleDismiss = async () => {
		track(AnalyticsEvent.TrialWhatsNext.Dismissed, { phase: data.phase, milestone: data.milestone });
		await dismissMilestone().catch(() => undefined);
		onClose();
	};

	// Acting on a CTA also dismisses the milestone so the admin isn't nagged
	// again after they've engaged.
	const handleActed = async () => {
		await dismissMilestone().catch(() => undefined);
		onClose();
	};

	return (
		<Box position="fixed" inset={0} zIndex="modal" bgColor="bg.canvas" overflowY="auto" data-testid="trial-whats-next-interstitial">
			<TrialWhatsNextView data={data} onActed={handleActed} compact />
			{!isPostExpiry && (
				<Box textAlign="center" pb={6}>
					<Text
						as="button"
						color="text.softer"
						fontSize="sm"
						onClick={handleDismiss}
						data-testid="trial-whats-next-dismiss"
						transition="color 0.15s ease"
						_hover={{ color: 'text.soft' }}
					>
						{t('interstitial.dismiss')}
					</Text>
				</Box>
			)}
		</Box>
	);
};
